/**
 * manage-projects.js
 * Domain use cases for creating, updating, deleting, and uploading project artifacts.
 */

export function createCreateProject({
  saveProject,
  normalizeFiles = defaultNormalizeFiles,
  metricsLogger = null,
  metricsStage = 'projects.create',
  now = defaultNow,
} = {}) {
  if (typeof saveProject !== 'function') {
    throw new Error('saveProject is required');
  }

  return async function createProject({
    name,
    instructions = '',
    files = [],
    color = null,
    teamType = null,
  } = {}) {
    const trimmedName = normalizeName(name);
    if (!trimmedName) {
      throw new Error('Project name is required');
    }

    const normalizedInstructions = normalizeInstructions(instructions);
    const normalizedFiles = normalizeFiles(files);

    const started = now();
    const payload = {
      name: trimmedName,
      instructions: normalizedInstructions,
      files: normalizedFiles,
    };
    if (color) payload.color = color;
    if (teamType) payload.teamType = teamType;
    const result = await saveProject(payload);
    const durationMs = elapsedSince(started, now);

    if (typeof metricsLogger === 'function') {
      safeInvoke(metricsLogger, {
        stage: metricsStage,
        durationMs,
        fileCount: normalizedFiles.length,
        payloadBytes: estimatePayloadBytes(normalizedFiles),
      });
    }

    return result;
  };
}

export function createUpdateProject({
  updateProject,
  normalizeFiles = defaultNormalizeFiles,
} = {}) {
  if (typeof updateProject !== 'function') {
    throw new Error('updateProject is required');
  }

  return async function updateProjectUseCase({
    teamId,
    data = {},
  } = {}) {
    const normalizedId = normalizeId(teamId);
    if (!normalizedId) {
      throw new Error('Project ID is required');
    }

    const payload = { ...data };

    if (typeof payload.name === 'string') {
      payload.name = payload.name.trim();
    }
    if (typeof payload.title === 'string' && !payload.name) {
      payload.name = payload.title.trim();
      delete payload.title;
    }
    if (typeof payload.instructions === 'string') {
      payload.instructions = payload.instructions.trim();
    }
    if (payload.files !== undefined) {
      payload.files = normalizeFiles(payload.files);
    }

    return updateProject(normalizedId, payload);
  };
}

export function createDeleteProject({
  deleteProject,
} = {}) {
  if (typeof deleteProject !== 'function') {
    throw new Error('deleteProject is required');
  }

  return async function deleteProjectUseCase({ teamId } = {}) {
    const normalizedId = normalizeId(teamId);
    if (!normalizedId) {
      throw new Error('Project ID is required');
    }

    return deleteProject(normalizedId);
  };
}

export function createUploadProjectFiles({
  uploadFiles,
  normalizeFiles = defaultNormalizeFiles,
} = {}) {
  if (typeof uploadFiles !== 'function') {
    throw new Error('uploadFiles is required');
  }

  return async function uploadProjectFilesUseCase({
    teamId,
    files = [],
  } = {}) {
    const normalizedId = normalizeId(teamId);
    if (!normalizedId) {
      throw new Error('Project ID is required');
    }

    const normalizedFiles = normalizeFiles(files);
    return uploadFiles(normalizedId, normalizedFiles);
  };
}

function normalizeName(value) {
  if (typeof value !== 'string') {
    return '';
  }
  return value.trim();
}

function normalizeInstructions(value) {
  if (typeof value !== 'string') {
    return '';
  }
  return value.trim();
}

function normalizeId(value) {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (value != null && typeof value.toString === 'function') {
    const stringified = value.toString().trim();
    return stringified || null;
  }
  return null;
}

function defaultNormalizeFiles(files = []) {
  if (!files) {
    return [];
  }
  if (Array.isArray(files)) {
    return files;
  }
  if (typeof files[Symbol.iterator] === 'function') {
    return Array.from(files);
  }
  return [files];
}

function defaultNow() {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
}

function elapsedSince(start, nowFn = defaultNow) {
  const end = nowFn();
  return Number.isFinite(start) ? Math.max(0, end - start) : undefined;
}

function estimatePayloadBytes(files) {
  if (!Array.isArray(files) || !files.length) {
    return 0;
  }
  return files.reduce((total, file) => total + (file?.size || 0), 0);
}

function safeInvoke(fn, payload) {
  try {
    fn(payload);
  } catch (error) {
    // Swallow metrics errors
  }
}

if (typeof window !== 'undefined') {
  window.AgileProDomain = window.AgileProDomain || {};
  window.AgileProDomain.projects = window.AgileProDomain.projects || {};
  window.AgileProDomain.projects.createCreateProject = createCreateProject;
  window.AgileProDomain.projects.createUpdateProject = createUpdateProject;
  window.AgileProDomain.projects.createDeleteProject = createDeleteProject;
  window.AgileProDomain.projects.createUploadProjectFiles = createUploadProjectFiles;
}

